import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import UnsavedChangesDialog from '../unsavedChanges/UnsavedChangesDialog';

export interface UnsavedChangesConfig {
  hasUnsavedChanges: () => boolean;
  save: () => Promise<void> | void;
  proceedAfterSave: boolean;
  canSave?: () => boolean;
}

interface UnsavedChangesDialogContextType {
  setUnsavedChangesWithId: (id: number, config: UnsavedChangesConfig | null) => void;
  confirmExitIfNeeded: (onExit: () => void) => void;
}

const UnsavedChangesDialogContext =
  createContext<UnsavedChangesDialogContextType>({
    setUnsavedChangesWithId: () => { },
    confirmExitIfNeeded: (onExit: () => void) => onExit(),
  });

export const UnsavedChangesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [configs, setConfigs] =
    useState<{ id: number; config: UnsavedChangesConfig }[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const setUnsavedChangesWithId = useCallback(
    (id: number, config: UnsavedChangesConfig | null) => {
      setConfigs((prev) => {
        const filtered = prev.filter((c) => c.id !== id);
        if (config) {
          return [...filtered, { id, config }];
        }
        return filtered;
      });
    },
    [],
  );

  const configsRef = useRef(configs);
  configsRef.current = configs;

  const confirmExitIfNeeded = useCallback(
    (onExit: () => void) => {
      const dirtyConfigs = configsRef.current.filter((c) =>
        c.config.hasUnsavedChanges(),
      );

      if (dirtyConfigs.length === 0) {
        onExit();
        return;
      }

      const anySavable = dirtyConfigs.some((c) =>
        c.config.canSave ? c.config.canSave() : true,
      );
      if (!anySavable) {
        setConfigs([]);
        onExit();
        return;
      }

      pendingActionRef.current = onExit;
      setOpen(true);
    },
    [],
  );

  const handleClose = () => {
    setOpen(false);
    pendingActionRef.current = null;
  };

  const handleDiscardAndExit = () => {
    setOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setConfigs([]);
    action && action();
  };

  const handleSaveAndExit = async () => {
    if (configs.length === 0) return;
    try {
      setSaving(true);
      // Save all dirty configs in reverse order (bottom-up in terms of editor hierarchy)
      for (let i = configs.length - 1; i >= 0; i--) {
        const { config } = configs[i];
        if (config.hasUnsavedChanges()) {
          await Promise.resolve(config.save());
        }
      }
    } finally {
      setSaving(false);
    }
    setOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    const proceedAfterSave = configs.every((c) => c.config.proceedAfterSave);
    setConfigs([]);
    if (proceedAfterSave) {
      action && action();
    }
  };

  const value = useMemo(
    () => ({ setUnsavedChangesWithId, confirmExitIfNeeded }),
    [setUnsavedChangesWithId, confirmExitIfNeeded],
  );

  return (
    <UnsavedChangesDialogContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        open={open}
        onClose={handleClose}
        onSaveAndExit={handleSaveAndExit}
        onDiscardAndExit={handleDiscardAndExit}
        saving={saving}
      />
    </UnsavedChangesDialogContext.Provider>
  );
};

export const useUnsavedChanges = () => {
  const { setUnsavedChangesWithId, confirmExitIfNeeded } = useContext(
    UnsavedChangesDialogContext,
  );
  const [id] = useState(() => Math.random());

  const setUnsavedChanges = useCallback(
    (config: UnsavedChangesConfig | null) => setUnsavedChangesWithId(id, config),
    [id, setUnsavedChangesWithId],
  );

  return useMemo(
    () => ({
      confirmExitIfNeeded,
      setUnsavedChanges,
    }),
    [confirmExitIfNeeded, setUnsavedChanges],
  );
};
